/**
 * FieldGraphDebugOverlay — draws the synthetic airfield network so the taxi
 * routing is visible and tunable. Renders the runway, the shared taxiway spine,
 * hold-short / gate nodes, and every aircraft's routed ground polyline.
 *
 * Field-graph geometry is in scene feet [x, z]; we convert each point to lng/lat
 * via CoordinateService and project to screen with `map.project()` on every
 * camera move (imperative SVG attribute updates — no React re-render per frame,
 * mirroring PlacementDebugOverlay). Pairs with the Top-down camera toggle.
 */
import { useEffect, useMemo, useRef } from 'react'
import { CoordinateService } from '@/services/3d/CoordinateService'
import type { FieldGraph } from '../data/fieldGraph'
import type { MovementPlan } from '../motion/movementPlan'
import type { Pt } from '../motion/easing'

interface FieldGraphDebugOverlayProps {
  map: mapboxgl.Map | null
  /** Scene origin [lng, lat]. */
  center: [number, number] | null
  fieldGraph: FieldGraph | undefined
  plans: Record<string, MovementPlan>
  visible: boolean
}

interface PolyShape {
  id: string
  pts: Pt[]
  color: string
  width: number
  dash?: string
  opacity: number
}
interface NodeShape {
  id: string
  pt: Pt
  color: string
  r: number
}

const LEG_COLORS: Record<string, string> = {
  taxi: '#22d3ee', // cyan
  tow: '#f59e0b', // amber
  takeoff_roll: '#f43f5e', // rose — accelerating on the runway
  landing_roll: '#a3e635', // lime — decelerating on the runway
}

/** Keep at most ~16 points per route so the overlay stays light during the exodus. */
function downsample(path: Pt[], max = 16): Pt[] {
  if (path.length <= max) return path
  const stride = Math.ceil(path.length / max)
  const out: Pt[] = []
  for (let i = 0; i < path.length; i += stride) out.push(path[i])
  if (out[out.length - 1] !== path[path.length - 1]) out.push(path[path.length - 1])
  return out
}

export function FieldGraphDebugOverlay({ map, center, fieldGraph, plans, visible }: FieldGraphDebugOverlayProps) {
  const { polys, nodes } = useMemo(() => {
    const polys: PolyShape[] = []
    const nodes: NodeShape[] = []
    if (!fieldGraph) return { polys, nodes }

    // Reference network.
    polys.push({ id: 'runway', pts: [fieldGraph.thresholdApproach, fieldGraph.thresholdDeparture], color: '#ffffff', width: 3, opacity: 0.85 })
    polys.push({ id: 'spine', pts: [fieldGraph.spine[0], fieldGraph.spine[1]], color: '#34d399', width: 2.5, dash: '2 6', opacity: 0.9 })
    polys.push({ id: 'final-in', pts: [fieldGraph.finalEntry, fieldGraph.thresholdApproach], color: '#60a5fa', width: 1.5, dash: '6 6', opacity: 0.6 })
    polys.push({ id: 'climb-out', pts: [fieldGraph.thresholdDeparture, fieldGraph.climbExit], color: '#60a5fa', width: 1.5, dash: '6 6', opacity: 0.6 })

    nodes.push({ id: 'thr-app', pt: fieldGraph.thresholdApproach, color: '#ffffff', r: 5 })
    nodes.push({ id: 'thr-dep', pt: fieldGraph.thresholdDeparture, color: '#ffffff', r: 5 })
    // On-runway roll geometry (only distinct from thresholds when a runway is authored).
    nodes.push({ id: 'touchdown', pt: fieldGraph.touchdownPoint, color: '#a3e635', r: 4 })
    nodes.push({ id: 'rollout-end', pt: fieldGraph.rolloutEnd, color: '#65a30d', r: 4 })
    nodes.push({ id: 'rotation', pt: fieldGraph.rotationPoint, color: '#f43f5e', r: 4 })
    fieldGraph.runwayAccess.forEach((p, i) => nodes.push({ id: `acc-${i}`, pt: p, color: '#fca5a5', r: 4 }))
    fieldGraph.gates.forEach((g) => nodes.push({ id: g.id, pt: g.center, color: '#34d399', r: 4 }))

    // Routed ground polylines (the thing we're fixing): taxi + tow legs.
    for (const id in plans) {
      for (let i = 0; i < plans[id].legs.length; i++) {
        const leg = plans[id].legs[i]
        const color = LEG_COLORS[leg.kind]
        if (!color || leg.path.length < 2) continue
        polys.push({ id: `${id}:${i}`, pts: downsample(leg.path), color, width: 1.25, opacity: 0.5 })
      }
    }
    return { polys, nodes }
  }, [fieldGraph, plans])

  const polyRefs = useRef<Map<string, SVGPolylineElement | null>>(new Map())
  const nodeRefs = useRef<Map<string, SVGCircleElement | null>>(new Map())

  useEffect(() => {
    if (!map || !center || !visible) return
    const toScreen = (pt: Pt): [number, number] => {
      const [lng, lat] = CoordinateService.scenePositionToLngLat([pt[0], 0, pt[1]], center)
      const p = map.project([lng, lat])
      return [p.x, p.y]
    }

    const update = () => {
      for (const s of polys) {
        const el = polyRefs.current.get(s.id)
        if (!el) continue
        el.setAttribute('points', s.pts.map((pt) => toScreen(pt).join(',')).join(' '))
      }
      for (const n of nodes) {
        const el = nodeRefs.current.get(n.id)
        if (!el) continue
        const [x, y] = toScreen(n.pt)
        el.setAttribute('cx', String(x))
        el.setAttribute('cy', String(y))
      }
    }

    let raf = 0
    const schedule = () => {
      if (raf) return
      raf = requestAnimationFrame(() => {
        raf = 0
        update()
      })
    }
    map.on('move', schedule)
    update()
    return () => {
      map.off('move', schedule)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [map, center, visible, polys, nodes])

  if (!visible || !fieldGraph) return null

  return (
    <svg className="pointer-events-none absolute inset-0 z-[6] h-full w-full overflow-visible">
      {polys.map((s) => (
        <polyline
          key={s.id}
          ref={(el) => polyRefs.current.set(s.id, el)}
          fill="none"
          stroke={s.color}
          strokeWidth={s.width}
          strokeOpacity={s.opacity}
          strokeDasharray={s.dash}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      ))}
      {nodes.map((n) => (
        <circle
          key={n.id}
          ref={(el) => nodeRefs.current.set(n.id, el)}
          r={n.r}
          fill={n.color}
          fillOpacity={0.9}
          stroke="#0b1220"
          strokeWidth={1}
        />
      ))}
    </svg>
  )
}
